import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import axios from 'axios';
import { Users as UsersIcon, Search, ArrowLeft, Plus } from 'react-feather';
import { toast } from 'react-toastify';
import config from '../../../../config';

import '../../../ControlPanel/sadmin.css';
import '../../Settings/settings.css';
import '../admin-v3.css';
import v3DataTableStyles from '../v3DataTable';
import v3SelectStyles from '../v3Select';

const portalProps = {
    menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
    menuPosition: 'fixed',
    styles: v3SelectStyles,
};

const ManageUsers = ({ embedded = false }) => {
    const { t } = useTranslation();
    const canManage = localStorage.getItem('manageUsers') === '1';
    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };
    const concessionaire_id = localStorage.getItem('concessionaireId');
    const login_id = localStorage.getItem('loginId');

    const [users, setUsers] = useState([]);
    const [userProfiles, setUserProfiles] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [viewType, setViewType] = useState(''); // '' | 'Add' | 'Edit'
    const [userId, setUserId] = useState('');
    const [userProfileId, setUserProfileId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!embedded && !canManage) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canManage, embedded]);

    const fetchUsers = useCallback(() => {
        setLoading(true);
        axios
            .post(`${config.hostname}/users/getuserbyconcessionaire`, { concessionaire_id }, configheader)
            .then((res) => setUsers(res.data || []))
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    useEffect(() => {
        axios
            .post(`${config.hostname}/userpermissions/getpermissionbyconcessionaire`, { concessionaire_id }, configheader)
            .then((res) => setUserProfiles(res.data || []))
            .catch((err) => console.log(err));
        axios
            .post(`${config.hostname}/property/getpropertybyconcessionaire`, { property_concessionaire_id: concessionaire_id }, configheader)
            .then((res) => setProperties(res.data || []))
            .catch((err) => console.log(err));
    }, []);

    const profileOptions = useMemo(
        () => userProfiles.map((p) => ({ value: p.user_type_id._id, label: p.user_type_id.user_role })),
        [userProfiles],
    );
    const propertyOptions = useMemo(
        () => properties.map((p) => ({ value: p._id, label: p.property_name })),
        [properties],
    );

    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) =>
            `${u.userFirstName || ''} ${u.userLastName || ''}`.toLowerCase().includes(q) ||
            (u.username || '').toLowerCase().includes(q) ||
            (u.user_type_id?.user_role || '').toLowerCase().includes(q));
    }, [users, search]);

    const clearAll = () => {
        setViewType('');
        setUserId('');
        setFirstName('');
        setLastName('');
        setEmail('');
        setUserProfileId('');
        setSelectedProperties([]);
    };

    const handleUserClick = (id) => {
        setViewType('Edit');
        setUserId(id);
        axios
            .get(`${config.hostname}/users/${id}`, configheader)
            .then((res) => {
                setFirstName(res.data.userFirstName);
                setLastName(res.data.userLastName);
                setEmail(res.data.username);
                setUserProfileId(res.data.user_type_id);
                setSelectedProperties(res.data.property_id || []);
            })
            .catch((err) => console.log(err));
    };

    const submit = (e) => {
        e.preventDefault();
        if (!userProfileId) {
            toast.warning(t('Select a user profile'));
            return;
        }
        if (selectedProperties.length === 0) {
            toast.warning(t('Select at least one property'));
            return;
        }
        const isEdit = viewType === 'Edit';
        const data = {
            userFirstName: firstName,
            userLastName: lastName,
            username: email,
            user_type_id: userProfileId,
            concessionaire_id,
            property_id: selectedProperties,
            login_id,
            ...(isEdit ? {} : { password: 'egoal@1' }),
        };
        const url = isEdit
            ? `${config.hostname}/users/updateuser/${userId}`
            : `${config.hostname}/users`;
        setSaving(true);
        axios
            .post(url, data, configheader)
            .then(() => {
                toast.success(isEdit ? t('Successfully updated') : t('Successfully added'));
                clearAll();
                fetchUsers(); // refresh list so it reflects the change
            })
            .catch((err) => {
                toast.error(t('Error'));
                console.log(err);
            })
            .finally(() => setSaving(false));
    };

    const columns = [
        { name: '#', selector: (row, i) => i + 1, width: '64px' },
        { name: t('Name'), selector: (r) => `${r.userFirstName || ''} ${r.userLastName || ''}`.trim(), sortable: true, wrap: true, minWidth: '180px' },
        { name: t('Email'), selector: (r) => r.username, sortable: true, wrap: true, minWidth: '200px' },
        { name: t('User Profile'), selector: (r) => r.user_type_id?.user_role || '—', sortable: true, minWidth: '140px' },
    ];

    if (!canManage) return null;

    const renderForm = (isEdit) => (
        <form className="v3-card" style={{ padding: 22 }} onSubmit={submit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                <div className="v3-field">
                    <label>{t('First Name')}</label>
                    <input className="form-control" type="text" placeholder={t('First name')} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                </div>
                <div className="v3-field">
                    <label>{t('Last Name')}</label>
                    <input className="form-control" type="text" placeholder={t('Last name')} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                </div>
                <div className="v3-field">
                    <label>{t('Email')}</label>
                    <input className="form-control" type="email" placeholder={t('Email')} value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="v3-field">
                    <label>{t('User Profile')}</label>
                    <Select
                        {...portalProps}
                        options={profileOptions}
                        value={profileOptions.find((o) => o.value === userProfileId) || null}
                        onChange={(opt) => setUserProfileId(opt ? opt.value : '')}
                        placeholder={t('Select profile…')}
                        isSearchable
                    />
                </div>
                <div className="v3-field" style={{ gridColumn: '1 / -1' }}>
                    <label>{t('Properties')}</label>
                    <Select
                        {...portalProps}
                        isMulti
                        options={propertyOptions}
                        value={propertyOptions.filter((o) => selectedProperties.includes(o.value))}
                        onChange={(opts) => setSelectedProperties((opts || []).map((o) => o.value))}
                        placeholder={t('Search & select properties…')}
                        closeMenuOnSelect={false}
                    />
                </div>
            </div>
            {!isEdit && (
                <p className="dash-muted" style={{ fontSize: 12, margin: '14px 0 0' }}>
                    {t('New users get a default password')} <code>egoal@1</code> {t('to change on first login.')}
                </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                    {saving ? t('Saving…') : isEdit ? t('Update user') : t('Add user')}
                </button>
            </div>
        </form>
    );

    const actions = viewType === '' ? (
        <>
            <label className="v3-sadmin-search">
                <Search size={14} />
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('Search users…')} aria-label={t('Search users')} />
            </label>
            <button type="button" className="v3-btn v3-btn-primary" onClick={() => setViewType('Add')}>
                <Plus size={15} /> {t('Add New')}
            </button>
        </>
    ) : (
        <button type="button" className="v3-btn v3-btn-ghost" onClick={clearAll}>
            <ArrowLeft size={15} /> {t('Back')}
        </button>
    );

    const body = (
        <>
            {viewType === '' && (
                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-sadmin-list-header">
                        <div className="v3-sadmin-list-title">
                            <h2 className="v3-title">{t('Users')}</h2>
                            <span className="v3-sadmin-list-count">{filteredUsers.length}</span>
                        </div>
                    </div>
                    <div style={{ padding: '4px 8px 8px' }}>
                        <DataTable
                            columns={columns}
                            data={filteredUsers}
                            progressPending={loading}
                            pagination
                            highlightOnHover
                            pointerOnHover
                            responsive
                            onRowClicked={(row) => handleUserClick(row._id)}
                            customStyles={v3DataTableStyles}
                            noDataComponent={t('No users found.')}
                        />
                    </div>
                </div>
            )}
            {viewType === 'Add' && renderForm(false)}
            {viewType === 'Edit' && renderForm(true)}
        </>
    );

    // Embedded in the Users & Roles host: no own shell/hero, actions in a sub-toolbar.
    if (embedded) {
        return (
            <Fragment>
                <div className="v3-um-subbar">{actions}</div>
                {body}
            </Fragment>
        );
    }

    return (
        <div className="v3-sadmin v3-admin-page">
            <div className="v3-sadmin-hero">
                <div className="v3-sadmin-hero-text">
                    <p className="v3-label" style={{ margin: 0 }}>{t('Admin settings')}</p>
                    <h1 className="v3-display v3-sadmin-greeting">
                        <span className="v3-sadmin-name">
                            {viewType === 'Add' ? t('Add User') : viewType === 'Edit' ? t('Edit User') : t('Users')}
                        </span>
                    </h1>
                    <p className="v3-body v3-sadmin-sub">{t('Manage user accounts, profiles and property access.')}</p>
                </div>
                <div className="v3-sadmin-hero-actions">
                    {actions}
                    <span className="v3-sadmin-kpi-icon accent" aria-hidden><UsersIcon size={18} /></span>
                </div>
            </div>
            {body}
        </div>
    );
};

export default ManageUsers;
